import { marker } from "@jsverse/transloco-keys-manager/marker";
import { dateUtil } from "src/app/support/date-util";
import { lastValueFrom } from "rxjs";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { MemberLight } from "src/app/backend/models/member/member";
import { getCustomerNominative } from "src/app/support/customer";
import { sntTranslate } from "src/app/support/translate";
import dayjs from "dayjs";
import { capitalize } from "radash";

export const MAX_SHIFT_END_AT: Date = dayjs(new Date(8640000000000000))
  .add(-1, "week")
  .endOf("week")
  .toDate();

export enum ShiftPeriodType {
  AVAILABLE = "available", // reperibile
  ON_CALL = "on-call", // di turno
  WORK_PERMIT = "work-permit", // permesso
  VACATION = "vacation", // ferie,
  REST = "rest" // riposo
}

export type ShiftPeriod = {
  startAt: string;
  endAt: string;
  type: ShiftPeriodType;
  tags?: string[];
};

export type ShiftDayValue =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export type ShiftDay = {
  day: ShiftDayValue;
  periods?: ShiftPeriod[];
  vacation?: boolean;
  rest?: boolean;
};

export interface Shift {
  id: string;
  staff: {
    id: string;
  };
  days: ShiftDay[];
  startAt: string;
  endAt: string;
  frequency: number;
  recurring: boolean;
  maxEnd: boolean;
  parent?: Shift;
}

export type ShiftLight = Shift;

export type ShiftModel = Pick<Shift, "id" | "startAt" | "endAt">;

export type ShiftCreate = Pick<
  Shift,
  "startAt" | "endAt" | "days" | "frequency"
>;

export type ShiftUpdate = Partial<Pick<Shift, "days" | "frequency" | "endAt">>;

export type ShiftClone = Pick<Shift, "startAt" | "endAt"> & { staffId: string };

export function shiftDayIndexToDay(index: number): ShiftDayValue {
  switch (index) {
    case 0:
      return "monday";
    case 1:
      return "tuesday";
    case 2:
      return "wednesday";
    case 3:
      return "thursday";
    case 4:
      return "friday";
    case 5:
      return "saturday";
    case 6:
      return "sunday";
    default: {
      throw new Error("shiftDayIndexToDay: invalid index");
    }
  }
}

export function shiftDayToIndex(shiftDay: ShiftDayValue): number {
  switch (shiftDay) {
    case "monday":
      return 0;
    case "tuesday":
      return 1;
    case "wednesday":
      return 2;
    case "thursday":
      return 3;
    case "friday":
      return 4;
    case "saturday":
      return 5;
    case "sunday":
      return 6;
    default: {
      throw new Error("shiftDayToIndex: invalid shiftDay");
    }
  }
}

export function shiftDayToHHmmFormat(
  day: ShiftDayValue,
  periods: ShiftPeriod[] | undefined,
  newPeriodsToConcat?: ShiftPeriod[]
): ShiftDay {
  return {
    day: day,
    periods: [
      ...(periods?.map((period: ShiftPeriod) => {
        return {
          startAt: needTranslateToHHMM(period.startAt)
            ? dateUtil(period.startAt).format("HH:mm")
            : period.startAt,
          endAt: needTranslateToHHMM(period.endAt)
            ? dateUtil(period.endAt).format("HH:mm")
            : period.endAt,
          type: period.type,
          tags: period.tags
        };
      }) || []),
      ...(newPeriodsToConcat || [])
    ]
  };
}

function needTranslateToHHMM(period: string): boolean {
  return period.length > 5;
}

export function isShiftRecurring(shift: Shift, date: string): boolean {
  return (
    dateUtil(shift.endAt).isAfter(dateUtil(date).endOf("week")) ||
    dateUtil(shift.startAt).isBefore(dateUtil(date).startOf("week"))
  );
}

export interface ShiftMemberPrintViewModel {
  nominative: string;
  dateRange: string;
  days: Array<{
    vacation: boolean;
    name: string;
    periods: Array<{
      type: string;
      startAt: string;
      endAt: string;
    }>;
  }>;
}

export interface ShiftMembersPrintViewModel {
  shifts: ShiftMemberPrintViewModel[];
  days: Array<{ name: string; value: string; label: string }>;
  dateRange: string;
  rows: Array<{
    nominative: string;
    daysMap: Record<
      string,
      {
        vacation: boolean;
        periods: Array<{
          type: string;
          startAt: string;
          endAt: string;
        }>;
      }
    >;
  }>;
}

export async function shiftToShiftMembersPrintViewModel({
  members,
  startDate,
  shiftsMap
}: {
  members: MemberLight[];
  startDate: string;
  shiftsMap: Record<string, Shift | undefined>;
}): Promise<ShiftMembersPrintViewModel> {
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday"
  ];

  return <ShiftMembersPrintViewModel>{
    shifts: [],
    labels: {
      vacation: sntTranslate(marker("Ferie")).toUpperCase(),
      rest: sntTranslate(marker("Riposo")).toUpperCase()
    },
    dateRange: `${sntTranslate(marker("Turni"))} ${sntTranslate(
      marker("Dal startAt al endAt"),
      {
        startAt: dateUtil(startDate).format("DD/MM/YYYY"),
        endAt: dateUtil(startDate).endOf("week").format("DD/MM/YYYY")
      }
    ).toLowerCase()}`,
    days: days.map((day: string, index: number) => {
      return {
        name: capitalize(sntTranslate(day)),
        value: day,
        label: dateUtil(startDate).add(index, "day").format("DD.MM")
      };
    }),
    rows: members.map((member: MemberLight) => {
      return {
        nominative: getCustomerNominative(member, true),
        daysMap: shiftsMap[member.id]
          ? days.reduce<
              Record<
                string,
                {
                  vacation: boolean;
                  rest: boolean;
                  periods: Array<{
                    type: string;
                    startAt: string;
                    endAt: string;
                  }>;
                }
              >
            >((accumulator, day) => {
              const shiftDay = shiftsMap[member.id]!.days.find(
                (shiftDay) => shiftDay.day === day
              );

              if (shiftDay) {
                accumulator[day] = {
                  vacation: !!shiftDay.vacation,
                  rest: !!shiftDay.rest,
                  periods:
                    shiftDay.periods?.map((period) => {
                      return {
                        type: period.type,
                        startAt: dateUtil(period.startAt).format("HH:mm"),
                        endAt: dateUtil(period.endAt).format("HH:mm")
                      };
                    }) || []
                };
              }

              return accumulator;
            }, {})
          : {}
      };
    })
  };
}

export async function shifToShiftMemberPrintModel(
  { shift, startDate }: { shift: Shift; startDate: string },
  {
    memberBackend
  }: {
    memberBackend: MemberBackend;
  }
): Promise<ShiftMemberPrintViewModel> {
  const member = await lastValueFrom(memberBackend.loadLight(shift.staff.id));

  return <ShiftMemberPrintViewModel>{
    nominative: getCustomerNominative(member, true),
    dateRange: sntTranslate(marker("Dal startAt al endAt"), {
      startAt: dateUtil(startDate).format("DD/MM/YYYY"),
      endAt: dateUtil(startDate).endOf("week").format("DD/MM/YYYY")
    }).toLowerCase(),
    days: shift.days.map((shiftDay: ShiftDay) => {
      return {
        vacation: !!shiftDay.vacation,
        name: sntTranslate(shiftDay.day),
        periods:
          shiftDay.periods?.map((period: ShiftPeriod) => {
            return {
              type: period.type,
              startAt: dateUtil(period.startAt).format("HH:mm"),
              endAt: dateUtil(period.endAt).format("HH:mm")
            };
          }) || []
      };
    })
  };
}
